import React, { useState, useEffect } from 'react';

interface StickyTabsProps {
    showStickyTabs: boolean;
    activeTab: string;
    scrollToSection: (sectionId: string) => void;
    project?: any;
}

const StickyTabs: React.FC<StickyTabsProps> = ({ 
    showStickyTabs, 
    activeTab, 
    scrollToSection,
    project
}) => {
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const scrollRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const { scrollLeft, clientWidth } = scrollRef.current;
            const scrollAmount = clientWidth * 0.6;
            scrollRef.current.scrollTo({
                left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div id="project-tabs-trigger" style={{ height: showStickyTabs ? (windowWidth < 768 ? '110px' : '64px') : 'auto' }}>
            <div className={`sticky-tabs-container transition-all duration-300 w-full z-[1010] bg-white/95 backdrop-blur-md ${showStickyTabs ? 'fixed top-0 left-0 shadow-md border-b-2 border-accent' : 'relative border-b border-gray-200'}`}>
                <div className="w-full px-[45px] flex flex-col md:flex-row items-center justify-between">
                    <div className="flex-grow w-full md:max-w-[70%]">
                        <div className="flex items-center group relative py-0">
                            {/* Left Arrow */}
                            <button 
                                onClick={() => scroll('left')}
                                className="hidden md:flex absolute left-[-40px] z-10 w-8 h-8 items-center justify-center rounded-full bg-white shadow-md border border-gray-100 hover:bg-gray-50 transition-all active:scale-90"
                                title="Scroll Left"
                            >
                                <i className="fa-solid fa-chevron-left text-xs text-gray-600"></i>
                            </button>

                            {/* Mobile Left Arrow */}
                            <button 
                                onClick={() => scroll('left')}
                                className="flex md:hidden sticky left-0 z-10 w-8 h-12 items-center justify-center bg-white/90 backdrop-blur-sm pr-2"
                            >
                                <i className="fa-solid fa-chevron-left text-xs text-gray-400"></i>
                            </button>

                            <div 
                                ref={scrollRef}
                                className="sticky-tabs-nav flex items-center gap-0.5 overflow-x-auto no-scrollbar py-2 w-full scroll-smooth"
                            >
                                {[
                                    { id: 'overview', label: 'Overview' },
                                    { id: 'key-highlights', label: 'Highlights' },
                                    { id: 'developer-details', label: project?.developer?.role === 'owner' ? 'Owner' : project?.developer?.role === 'broker' ? 'Broker' : 'Developer' },
                                    { id: 'about-project', label: 'About' },
                                    { id: 'floor-plans', label: 'Plans' },
                                    { id: 'location-details', label: 'Details' },
                                    { id: 'project-location', label: 'Map' },
                                    { id: 'nearby-landmarks', label: 'Landmarks' },
                                    { id: 'gallery', label: 'Gallery' },
                                    { id: 'media-tour', label: 'Video/360°' },
                                    { id: 'price-trends', label: 'Trends' },
                                    { id: 'legal-documents', label: 'Legal' },
                                    { id: 'amenities', label: 'Amenities' },
                                    { id: 'specifications', label: 'Specs' },
                                    { id: 'faq-section', label: 'FAQ' },
                                    { id: 'reviews', label: 'Reviews' }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => scrollToSection(tab.id)}
                                        className={`inline-block text-center cursor-pointer transition-all px-3 py-1.5 md:px-5 md:py-2.5 rounded-full font-black transition-all whitespace-nowrap text-decoration-none text-[12px] md:text-[13px] ${activeTab === tab.id ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] shadow-lg scale-105 ring-2 ring-[#f1ae24]/20' : 'text-[#05144b] hover:bg-[#05144b]/5 hover:text-[#4c1d95] opacity-70 hover:opacity-100'}`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* Mobile Right Arrow */}
                            <button 
                                onClick={() => scroll('right')}
                                className="flex md:hidden sticky right-0 z-10 w-8 h-12 items-center justify-center bg-white/90 backdrop-blur-sm pl-2"
                            >
                                <i className="fa-solid fa-chevron-right text-xs text-gray-400"></i>
                            </button>

                            {/* Right Arrow */}
                            <button 
                                onClick={() => scroll('right')}
                                className="hidden md:flex absolute right-[0px] z-10 w-8 h-8 items-center justify-center rounded-full bg-white shadow-md border border-gray-100 hover:bg-gray-50 transition-all active:scale-90"
                                title="Scroll Right"
                            >
                                <i className="fa-solid fa-chevron-right text-xs text-gray-600"></i>
                            </button>
                        </div>
                    </div>

                    {/* Submit Inquiry Button */}
                    <div className="flex-shrink-0 w-full md:w-[30%] pb-2 md:pb-0 md:pl-4 flex justify-end">
                        <button 
                            onClick={() => scrollToSection('inquiry-section')}
                            className="w-full relative overflow-hidden bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] font-black text-[12px] md:text-[14px] px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl hover:opacity-90 hover:-translate-y-0.5 active:scale-95 transition-all flex items-center justify-center gap-2 whitespace-nowrap animate-pulse-button"
                        >
                            {/* Continuous Shimmer Effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer-continuous pointer-events-none"></div>
                            
                            <i className="fa-solid fa-paper-plane text-[10px] animate-plane-fly"></i>
                            <span className="relative z-10 uppercase tracking-wider">Submit Inquiry</span>
                        </button>

                        <style>{`
                            @keyframes plane-fly {
                                0% { transform: translate(0, 0) rotate(0); opacity: 1; }
                                20% { transform: translate(2px, -2px) rotate(-5deg); }
                                50% { transform: translate(15px, -15px) rotate(-10deg); opacity: 0; }
                                51% { transform: translate(-15px, 15px) rotate(-10deg); opacity: 0; }
                                100% { transform: translate(0, 0) rotate(0); opacity: 1; }
                            }
                            .animate-plane-fly {
                                animation: plane-fly 3s infinite ease-in-out;
                            }
                            @keyframes shimmer-continuous {
                                0% { transform: translateX(-150%); }
                                100% { transform: translateX(150%); }
                            }
                            .animate-shimmer-continuous {
                                animation: shimmer-continuous 3s infinite linear;
                            }
                            @keyframes pulse-button {
                                0%, 100% { transform: scale(1); box-shadow: 0 10px 15px -3px rgba(5, 20, 75, 0.3); }
                                50% { transform: scale(1.02); box-shadow: 0 20px 25px -5px rgba(5, 20, 75, 0.4); }
                            }
                            .animate-pulse-button {
                                animation: pulse-button 3s ease-in-out infinite;
                            }
                        `}</style>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default StickyTabs;

